import { QueryClient, useSuspenseQueries } from '@tanstack/react-query';
import { useMemo } from 'react';

import { type LinkParagraaf } from '@/api/queries/objects';
import { useVSPB } from '@/api/queries/objects';
import { createInString } from '@/utils/createInString';

import { EditorView } from '../-store/useEditorStore';
import { type LabelEditorFilter } from '../-store/useLabelEditorStore';
import { useEditorLinks } from './useEditorLinks';

type UseEditorLinksWithLabelProps = {
  hoofdstukId: string;
  besluitId: string;
  labelFilter: LabelEditorFilter;
  multiSelectVoorschriftIdLog?: Set<string>;
};

export const useEditorLinksWithLabelSuspense = ({
  hoofdstukId,
  besluitId,
  labelFilter,
  multiSelectVoorschriftIdLog = new Set<string>(),
}: UseEditorLinksWithLabelProps) => {
  const linksInHoofdstukOptions = useMemo(
    () =>
      editorLinksQueryOptions({
        hoofdstukId,
        besluitId,
      }),
    [hoofdstukId, besluitId]
  );

  const voorschriftIdsWithFilterOptions = useMemo(
    () =>
      voorschriftIdsWithFilterQueryOptions({
        besluitId,
        labelFilter,
      }),
    [besluitId, labelFilter]
  );

  const [links, voorschriftIds] = useSuspenseQueries({
    queries: [linksInHoofdstukOptions, voorschriftIdsWithFilterOptions],
  });

  return useMemo(
    () =>
      filterLinksByVoorschriftIds({
        links: links.data,
        voorschriftIds: voorschriftIds.data,
        multiSelectVoorschriftIdLog,
      }),
    [links.data, voorschriftIds.data, multiSelectVoorschriftIdLog]
  );
};

export const ensureQueryDataEditorLinksWithLabel = (
  queryClient: QueryClient,
  options: UseEditorLinksWithLabelProps
) => {
  return Promise.all([
    queryClient.ensureQueryData(editorLinksQueryOptions(options)),
    queryClient.ensureQueryData(voorschriftIdsWithFilterQueryOptions(options)),
  ]);
};

const editorLinksQueryOptions = (
  props: Pick<UseEditorLinksWithLabelProps, 'hoofdstukId' | 'besluitId'>
) => {
  return useEditorLinks.getOptions({
    hoofdstukId: props.hoofdstukId,
    besluitId: props.besluitId,
    view: EditorView.DEFAULT,
  });
};

const voorschriftIdsWithFilterQueryOptions = ({
  besluitId,
  labelFilter,
}: {
  besluitId: string;
  labelFilter: LabelEditorFilter;
}) => {
  return useVSPB.getOptions(
    {
      page: 1,
      size: 1000,
      filter: [
        {
          model: 'VSPB',
          field: 'Besluit',
          op: 'eq',
          value: besluitId,
        },
        labelFilter.type === 'labels'
          ? {
              model: 'Label',
              field: 'ID',
              op: 'in',
              value: createInString(labelFilter.labelIds),
            }
          : {
              model: 'Voorschrift',
              field: 'nolabel',
              op: 'eq',
              value: 'true',
            },
      ],
    },
    {
      select: ({ objects }) =>
        new Set(objects?.map((VSPB) => VSPB.Voorschrift) ?? []),
    }
  );
};

const filterLinksByVoorschriftIds = ({
  links,
  voorschriftIds,
  multiSelectVoorschriftIdLog = new Set<string>(),
}: {
  links: LinkParagraaf[];
  voorschriftIds: Set<string>;
  multiSelectVoorschriftIdLog?: Set<string>;
}): LinkParagraaf[] => {
  return links
    .map((paragraafLink) => {
      const subparagrafen = paragraafLink.subparagrafen
        .map((subparagraafLink) => {
          const voorschriften = subparagraafLink.voorschriften.filter(
            (voorschriftLink) =>
              voorschriftIds.has(voorschriftLink.VSPB.Voorschrift) ||
              multiSelectVoorschriftIdLog.has(voorschriftLink.VSPB.Voorschrift)
          );

          if (voorschriften.length === 0) {
            return null;
          }

          return {
            ...subparagraafLink,
            voorschriften,
          };
        })
        .filter((subparagraafLink) => subparagraafLink !== null);

      if (subparagrafen.length === 0) {
        return null;
      }

      return {
        ...paragraafLink,
        subparagrafen,
      };
    })
    .filter((paragraafLink) => paragraafLink !== null);
};
